package dev.lingvo.site.pages.app.projects.slug.settings

import dev.lingvo.shared.BillingInterval
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.Money
import dev.lingvo.shared.Plan
import dev.lingvo.shared.Role
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf

internal data class WorkspacePlanCardPresentation(
    val plan: Plan,
    val title: String,
    val price: Money,
    val interval: BillingInterval,
    val actionLabel: String,
    val actionEnabled: Boolean,
    val current: Boolean,
)

internal data class WorkspacePlanPresentation(
    val currentPlanName: String,
    val currentPrice: String,
    val currentPriceCadence: String?,
    val permissionNote: String?,
    val creditsRemaining: Int,
    val zeroCreditNote: String?,
    val actionsBlocked: Boolean,
    val cards: List<WorkspacePlanCardPresentation>,
)

internal fun workspacePlanPresentation(
    billing: BillingOverviewDto,
    role: Role?,
): WorkspacePlanPresentation {
    val canAct = role == Role.OWNER
    val actionsBlocked = billing.pendingCheckoutStartedAt != null || billing.degraded
    return WorkspacePlanPresentation(
        currentPlanName = billing.plan.displayName,
        currentPrice = if (billing.plan == Plan.FREE) {
            tr("app.projects.slug.settings.workspace_plan_presentation.free", "Free")
        } else {
            billing.listPricePerSeat.display
        },
        currentPriceCadence = if (billing.plan == Plan.FREE) {
            null
        } else {
            when (billing.interval) {
                BillingInterval.MONTH ->
                    tr("app.projects.slug.settings.workspace_plan_presentation.per_month", "per month")
                BillingInterval.YEAR ->
                    tr("app.projects.slug.settings.workspace_plan_presentation.per_year", "per year")
            }
        },
        // The reason, not just the rule — the same sentence `billing/Plans.kt` carries, so the two
        // surfaces that show plan cards answer "who may change this" the same way.
        permissionNote = if (canAct) {
            null
        } else {
            tr(
                "app.projects.slug.settings.workspace_plan_presentation.permission_note",
                "You can see everything here. Starting a subscription commits a card, and that is " +
                    "the owner's decision to make.",
            )
        },
        creditsRemaining = billing.creditsRemaining,
        zeroCreditNote = if (billing.creditsRemaining == 0) {
            tr(
                "app.projects.slug.settings.workspace_plan_presentation.zero_credit_note",
                "Lingvo-funded machine translation pauses at zero credits. Translating by hand, " +
                    "import, export, review and QA still work.",
            )
        } else {
            null
        },
        actionsBlocked = actionsBlocked,
        cards = billing.planOffers.map { offer ->
            val current = offer.plan == billing.plan
            val actionEnabled = canAct && !actionsBlocked && !current && offer.plan != Plan.FREE &&
                !billing.hasSubscription && offer.available
            WorkspacePlanCardPresentation(
                plan = offer.plan,
                title = offer.plan.displayName,
                price = offer.pricePerSeat,
                interval = offer.interval,
                // The words on the one button that takes money. `unavailableReason` stays as the
                // server sent it: it is a specific sentence about this workspace, and replacing it
                // with a generic translated one would lose the reason.
                actionLabel = when {
                    current -> tr("app.projects.slug.settings.workspace_plan_presentation.action_active", "Active for this workspace")
                    offer.plan == Plan.FREE ->
                        offer.unavailableReason ?: tr("app.projects.slug.settings.workspace_plan_presentation.action_free", "Included without a card")
                    billing.pendingCheckoutStartedAt != null ->
                        tr("app.projects.slug.settings.workspace_plan_presentation.action_checkout_pending", "Checkout confirmation pending")
                    billing.degraded -> tr("app.projects.slug.settings.workspace_plan_presentation.action_degraded", "Plan changes unavailable")
                    billing.hasSubscription -> tr("app.projects.slug.settings.workspace_plan_presentation.action_contact_support", "Contact support to switch")
                    !offer.available ->
                        offer.unavailableReason ?: tr("app.projects.slug.settings.workspace_plan_presentation.action_unavailable", "Unavailable")
                    else -> trf("app.projects.slug.settings.workspace_plan_presentation.action_choose", "Choose {plan}", "plan" to offer.plan.displayName)
                },
                actionEnabled = actionEnabled,
                current = current,
            )
        },
    )
}
